import * as React from 'react';
import classnames from 'classnames';

import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';

import { __t } from 'utils/language';

import type { DeliverableV2TabKey, MixedSelectDropdownItem } from 'types';
import type { FilterLinksItem } from 'components/filters/filter-links/types';
import TabsUnderlined from 'components/tabs/tabs-underlined/TabsUnderlined';

import { SyntheticEvent } from 'react';

const FILTER_VALUES = ['overdue_content', 'overdue_insights'];
const EXPANDED = 'expanded';
const COLLAPSED = 'collapsed';

type Props = {
  isForBrand?: boolean,
  isLoading?: boolean,
  contentApprovalV2: boolean,
  sortOptions: Array<string>,
  sort?: string,
  filter?: string,
  tags: Array<MixedSelectDropdownItem>,
  selectedViewIdsMap: {
    [id: number]: DeliverableV2TabKey
  },
  expandedIdsMap: {
    [id: number]: boolean
  },
  selectViewItems: Array<FilterLinksItem>,
  zoutfitsOnly: boolean,
  onTagFilterChange: (updatedList: Array<MixedSelectDropdownItem>) => void,
  onSortChanged: (sort: string) => void,
  onListFilterChanged: (filter?: string) => void,
  onChangeView: (selectedView: DeliverableV2TabKey) => void,
  onExpandAllChange: (isExpanded: boolean) => void
};

const CampaignDeliverablesTaskControl = (props: Props): JSX.Element => {
  const {
    isForBrand,
    isLoading,
    contentApprovalV2,
    selectViewItems,
    sortOptions,
    sort,
    filter,
    tags,
    selectedViewIdsMap,
    expandedIdsMap,
    zoutfitsOnly,
    onTagFilterChange,
    onSortChanged,
    onListFilterChanged,
    onChangeView,
    onExpandAllChange
  } = props;
  const controlsClasses = classnames('campaign-deliverables__task-controls', {
    'campaign-deliverables__task-controls--disabled': isLoading,
    'campaign-deliverables__task-controls--v2': contentApprovalV2
  });

  const topContainerClasses = classnames('campaign-deliverables__controls-container', {
    'campaign-deliverables__controls-container--space-between': !contentApprovalV2
  });

  const sortByLabelClasses = classnames('campaign-deliverables__control-with-label', {
    'campaign-deliverables__control-with-label--with-padding': contentApprovalV2
  });

  const handleResize = (event: SyntheticEvent<HTMLElement>) => {
    const target = event.currentTarget;
    const type = target.dataset.arg || '';

    onExpandAllChange(type === 'true');
  };

  const handleViewChange = (event: SyntheticEvent<HTMLElement>) => {
    const target = event.currentTarget;
    const type = target.dataset.arg as DeliverableV2TabKey;

    onChangeView(type);
  };

  const tabItems = [EXPANDED, COLLAPSED]
    .map(type => ({
      handleClick: handleResize,
      handleClickArg: type === EXPANDED,
      label: __t(`campaign_deliverables.expand_state.${type}`)
    }));

  return (
    <div className={controlsClasses}>
      <div className={topContainerClasses}>
        <div
          className="campaign-deliverables__control-with-label campaign-deliverables__control-with-label--with-padding"
        >
          <div className={contentApprovalV2 ? 'campaign-deliverables__tag-label' : ''}>
            {__t('campaign_deliverables.tag_filter.label')}
          </div>
          <MixedSelectDropdown
            items={tags}
            name="campaign-deliverables-tag-filter"
            withMulti
            withColors
            withCheckboxes
            withSortable
            withRemovable
            withSearchable
            placeholder={__t('campaign_deliverables.tag_filter.placeholder')}
            onItemsToggle={onTagFilterChange}
            onItemsRemove={onTagFilterChange}
            onItemsRemoveAll={onTagFilterChange}
            className="campaign-deliverables__tag-select"
          />
        </div>
        {
          isForBrand && (
            <div className={sortByLabelClasses}>
              <div className="campaign-deliverables__sort-label">
                {__t('campaign_deliverables.sort_by_label')}
              </div>
              <MixedSelectDropdown
                items={sortOptions
                  .map(sortValue => ({
                    identifier: sortValue,
                    isDisabled: false,
                    isSelected: sort === sortValue,
                    value: sortValue,
                    label: __t(`campaign_deliverables.sort_options.${sortValue}`)
                  }))
                }
                name="campaign-deliverables-sort"
                onItemsToggle={(updatedItems, data, selectedItem) => {
                  const sortValue = selectedItem?.value;
                  onSortChanged(sortValue || sortOptions[0]);
                }}
                className="campaign-deliverables__sort-select"
              />
            </div>
          )
        }
        {contentApprovalV2 && isForBrand && (
          <div className={sortByLabelClasses}>
            <div className="campaign-deliverables__sort-label">
              {__t('campaign_deliverables.filter_by_label')}
            </div>
            <MixedSelectDropdown
              items={FILTER_VALUES
                .filter(value => !(zoutfitsOnly && value === 'overdue_insights'))
                .map(sortValue => ({
                  identifier: sortValue,
                  isDisabled: false,
                  isSelected: filter === sortValue,
                  value: sortValue,
                  label: __t(`campaign_deliverables.filter_options.${sortValue}`)
                }))
              }
              name="campaign-deliverables-filter"
              withRemovable
              onItemsToggle={(updatedItems, data, selectedItem) => {
                const sortValue = selectedItem?.value;
                onListFilterChanged(sortValue);
              }}
              onItemsRemoveAll={() => {
                onListFilterChanged();
              }}
              className="campaign-deliverables__filter-select"
            />
          </div>
        )}
      </div>
      {contentApprovalV2 && (
        <React.Fragment>
          <VerticalSpacing type="large" />
          <div
            className="campaign-deliverables__controls-container campaign-deliverables__controls-container--space-between"
          >
            <div className="campaign-deliverables__sort">
              <div className="campaign-deliverables__view-label">
                {__t('campaign_deliverables.view_filter.label')}
              </div>
              <TabsUnderlined
                items={selectViewItems.map(item => ({
                  ...item,
                  handleClickArg: item.identifier,
                  handleClick: handleViewChange
                }))}
                toggleIdsMap={selectedViewIdsMap}
              />
            </div>
            <div className="">
              <TabsUnderlined
                items={tabItems}
                toggleIdsMap={expandedIdsMap}
              />
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default CampaignDeliverablesTaskControl;
